import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { config } from '@/lib/config';
import { useSubscriptionValidation } from '@/hooks/useSubscriptionValidation';
import { useDocumentStatus } from '@/hooks/useDocumentStatus';

interface DocumentUploadResult {
  uploadDocument: (file: File, topicId: string) => Promise<any>;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  isProcessing: boolean;
  progressPhase: 'uploading' | 'processing' | 'complete';
}

export const useDocumentUpload = (): DocumentUploadResult => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressPhase, setProgressPhase] = useState<'uploading' | 'processing' | 'complete'>('uploading');
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const { toast } = useToast();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const { validateCreditsForAction } = useSubscriptionValidation();
  
  // Subscribe to realtime document status updates
  const { status, isComplete } = useDocumentStatus(currentDocumentId);
  
  // Update progress based on realtime status
  useEffect(() => {
    if (currentDocumentId && status) {
      setUploadProgress(status.processing_progress);
      
      // Update phase based on status
      if (status.processing_status === 'uploading') {
        setProgressPhase('uploading');
        setIsUploading(true);
        setIsProcessing(false);
      } else if (['uploaded', 'extracting', 'chunking', 'indexing'].includes(status.processing_status)) {
        setProgressPhase('processing');
        setIsUploading(false);
        setIsProcessing(true);
      } else if (status.processing_status === 'ready') {
        setProgressPhase('complete');
        setIsUploading(false);
        setIsProcessing(false);
      } else if (status.processing_status === 'failed') {
        setError(status.processing_message);
        setIsUploading(false);
        setIsProcessing(false);
      }
    }
  }, [status, currentDocumentId]);

  // Handle completion separately - invalidate queries and show toast
  useEffect(() => {
    if (isComplete && currentDocumentId) {
      console.log('📡 Document processing complete, invalidating queries');
      
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['topic-documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['topic-library-items'] });
      
      // Show completion toast
      toast({
        title: "Processing complete!",
        description: "Your document is ready to study.",
      });
      
      // Reset document ID after a short delay
      setTimeout(() => {
        setCurrentDocumentId(null);
      }, 1000);
    }
  }, [isComplete, currentDocumentId, queryClient, toast]);

  const uploadDocument = async (file: File, topicId: string): Promise<any> => {
    if (!session) {
      throw new Error('User must be authenticated to upload documents');
    }

    // Validate credits before upload
    const creditsValidation = validateCreditsForAction();
    if (!creditsValidation.canProceed) {
      // Trigger out of credits dialog
      window.dispatchEvent(new CustomEvent('open_out_of_credits_dialog'));
      throw new Error('No credits remaining. Please upgrade to continue.');
    }

    setIsUploading(true);
    setIsProcessing(false);
    setUploadProgress(0);
    setError(null);
    setProgressPhase('uploading');

    return new Promise((resolve, reject) => {
      console.log('📁 Starting document upload for file:', file.name, 'to topic:', topicId);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('topic_id', topicId);

      const xhr = new XMLHttpRequest();

      // Track upload progress (Phase 1: 0-50%)
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const uploadProgress = Math.round((event.loaded / event.total) * 50); // Upload is 50% of total
          setUploadProgress(uploadProgress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            
            if (!result.success) {
              setIsUploading(false);
              setIsProcessing(false);
              throw new Error(result.message || 'Upload failed');
            }

            console.log('✅ Document upload request completed:', result);

            // Immediately invalidate queries so document shows in library while processing
            queryClient.invalidateQueries({ queryKey: ['topic-library-items'] });
            queryClient.invalidateQueries({ queryKey: ['documents'] });

            // Set document ID to start tracking realtime progress
            // The useEffect hooks will handle the rest
            if (result.document_id) {
              setCurrentDocumentId(result.document_id);
            }

            // Resolve immediately - completion will be handled by useEffect
            resolve(result);

          } catch (parseError) {
            setIsUploading(false);
            setIsProcessing(false);
            const error = new Error('Failed to parse server response');
            setError(error.message);
            reject(error);
          }
        } else {
          // Handle HTTP error responses
          setIsUploading(false);
          setIsProcessing(false);
          try {
            const errorData = JSON.parse(xhr.responseText);
            const error = new Error(errorData.error || `Upload failed with status ${xhr.status}`);
            setError(error.message);
            reject(error);
          } catch (parseError) {
            const error = new Error(`Upload failed with status ${xhr.status}`);
            setError(error.message);
            reject(error);
          }
        }
      });

      xhr.addEventListener('error', () => {
        setIsUploading(false);
        setIsProcessing(false);
        const error = new Error('Network error occurred during upload');
        setError(error.message);
        
        toast({
          title: "Upload failed",
          description: "Network error occurred. Please try again.",
          variant: "destructive",
        });
        
        reject(error);
      });

      xhr.addEventListener('abort', () => {
        setIsUploading(false);
        setIsProcessing(false);
        const error = new Error('Upload was cancelled');
        setError(error.message);
        reject(error);
      });

      xhr.open('POST', `${config.BACKEND_API_URL}/api/upload-pdf`);
      xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
      xhr.send(formData);
    });
  };

  return {
    uploadDocument,
    isUploading,
    uploadProgress,
    error,
    isProcessing,
    progressPhase,
  };
};